import { LpfrTab } from "./LpfrTab";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Mail, ShieldCheck, Send, Copy, Link2, ScanBarcode, CreditCard, Printer, Truck, MonitorSmartphone } from "lucide-react";
import { PfrMode, SefMode, SkuFormat, EmailTemplateDesign, PaymentMethod, PosQuantityMode } from "@finora/types";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";
import { BankAccountsSection } from "./BankAccountsSection";
import { MinimaxTab } from "./MinimaxTab";
import { IpsPgwTab } from "./IpsPgwTab";
import { CardPaymentsTab } from "./CardPaymentsTab";
import { PrintAgentTab } from "./PrintAgentTab";
import { CourierSettingsTab } from "./CourierSettingsTab";
import { FiscalDevicesSection } from "./FiscalDevicesSection";
import { AccountTab } from "./AccountTab";
import { DevicesTab } from "./DevicesTab";

type TabKey =
  | "firma"
  | "nalog"
  | "email"
  | "fiskalizacija"
  | "sef"
  | "minimax"
  | "ips-pgw"
  | "kartice"
  | "print-agent"
  | "kase"
  | "couriers";

const TABS: { key: TabKey; label: string; icon: typeof Building2 }[] = [
  { key: "firma", label: "Firma", icon: Building2 },
  { key: "nalog", label: "Nalog", icon: ShieldCheck },
  { key: "email", label: "Email (SMTP)", icon: Mail },
  { key: "fiskalizacija", label: "Fiskalizacija", icon: ShieldCheck },
  { key: "sef", label: "eFakture (SEF)", icon: Send },
  { key: "minimax", label: "MiniMax", icon: Link2 },
  { key: "ips-pgw", label: "IPS QR Naplata", icon: ScanBarcode },
  { key: "kartice", label: "Kartice", icon: CreditCard },
  { key: "print-agent", label: "Print Agent", icon: Printer },
  { key: "kase", label: "Kase i uređaji", icon: MonitorSmartphone },
  { key: "couriers", label: "Kurirske službe", icon: Truck },
];

function FirmaTab() {
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });
  const { data: pfrSettings } = useQuery({
    queryKey: ["pfr-settings"],
    queryFn: () => api.getPfrSettings(),
  });

  const [name, setName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [isVatRegistered, setIsVatRegistered] = useState(true);
  const [showImagesOnQuotes, setShowImagesOnQuotes] = useState(true);
  const [showImagesOnInvoices, setShowImagesOnInvoices] = useState(true);
  const [bankAccount, setBankAccount] = useState("");
  const [skuFormat, setSkuFormat] = useState<SkuFormat>(SkuFormat.NUMERIC);
  const [invoiceNumberPrefix, setInvoiceNumberPrefix] = useState("FAK");
  const [quoteNumberPrefix, setQuoteNumberPrefix] = useState("PON");
  const [deliveryNoteNumberPrefix, setDeliveryNoteNumberPrefix] = useState("OTP");
  const [includeYearInNumber, setIncludeYearInNumber] = useState(true);
  const [invoiceNumberStart, setInvoiceNumberStart] = useState("");
  const [quoteNumberStart, setQuoteNumberStart] = useState("");
  const [showLogoOnReceipt, setShowLogoOnReceipt] = useState(false);
  const [posQuantityMode, setPosQuantityMode] = useState<PosQuantityMode>(PosQuantityMode.WHOLE);
  const [quoteAcceptanceText, setQuoteAcceptanceText] = useState("");
  const [defaultQuoteNote, setDefaultQuoteNote] = useState("");
  const [defaultQuotePaymentTerms, setDefaultQuotePaymentTerms] = useState("");
  const [defaultInvoiceNote, setDefaultInvoiceNote] = useState("");
  const [defaultInvoicePaymentTerms, setDefaultInvoicePaymentTerms] = useState("");
  const [defaultDeliveryNoteNote, setDefaultDeliveryNoteNote] = useState("");
  const [usesWarehouse, setUsesWarehouse] = useState(false);
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const uploadLogo = useMutation({
    mutationFn: (file: File) => api.uploadCompanyLogo(file),
    onSuccess: (updated) => queryClient.setQueryData(["company-settings"], updated),
  });

  useEffect(() => {
    if (settings) {
      setName(settings.name);
      setTaxId(settings.taxId);
      setRegistrationNumber(settings.registrationNumber);
      setLocationName(settings.businessLocationName);
      setLocationAddress(settings.businessLocationAddress);
      setIsVatRegistered(settings.isVatRegistered);
      setShowImagesOnQuotes(settings.showProductImagesOnQuotes);
      setShowImagesOnInvoices(settings.showProductImagesOnInvoices);
      setBankAccount(settings.bankAccount ?? "");
      setSkuFormat(settings.skuFormat);
      setInvoiceNumberPrefix(settings.invoiceNumberPrefix);
      setQuoteNumberPrefix(settings.quoteNumberPrefix);
      setDeliveryNoteNumberPrefix(settings.deliveryNoteNumberPrefix);
      setIncludeYearInNumber(settings.includeYearInNumber);
      setInvoiceNumberStart(settings.invoiceNumberStart ? String(settings.invoiceNumberStart) : "");
      setQuoteNumberStart(settings.quoteNumberStart ? String(settings.quoteNumberStart) : "");
      setShowLogoOnReceipt(settings.showLogoOnReceipt);
      setPosQuantityMode(settings.posQuantityMode);
      setQuoteAcceptanceText(settings.quoteAcceptanceText ?? "");
      setDefaultQuoteNote(settings.defaultQuoteNote ?? "");
      setDefaultQuotePaymentTerms(settings.defaultQuotePaymentTerms ?? "");
      setDefaultInvoiceNote(settings.defaultInvoiceNote ?? "");
      setDefaultInvoicePaymentTerms(settings.defaultInvoicePaymentTerms ?? "");
      setDefaultDeliveryNoteNote(settings.defaultDeliveryNoteNote ?? "");
      setUsesWarehouse(settings.usesWarehouse);
      setStreet(settings.street ?? "");
      setHouseNumber(settings.houseNumber ?? "");
      setPostalCode(settings.postalCode ?? "");
      setCity(settings.city ?? "");
      setPhone(settings.phone ?? "");
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      api.updateCompanySettings({
        name,
        taxId,
        registrationNumber,
        businessLocationName: locationName,
        businessLocationAddress: locationAddress,
        isVatRegistered,
        showProductImagesOnQuotes: showImagesOnQuotes,
        showProductImagesOnInvoices: showImagesOnInvoices,
        bankAccount,
        skuFormat,
        invoiceNumberPrefix,
        quoteNumberPrefix,
        deliveryNoteNumberPrefix,
        includeYearInNumber,
        invoiceNumberStart: invoiceNumberStart ? Number(invoiceNumberStart) : null,
        quoteNumberStart: quoteNumberStart ? Number(quoteNumberStart) : null,
        showLogoOnReceipt,
        posQuantityMode,
        quoteAcceptanceText,
        defaultQuoteNote,
        defaultQuotePaymentTerms,
        defaultInvoiceNote,
        defaultInvoicePaymentTerms,
        defaultDeliveryNoteNote,
        usesWarehouse,
        street,
        houseNumber,
        postalCode,
        city,
        phone,
      }),
    onSuccess: (updated) => queryClient.setQueryData(["company-settings"], updated),
  });

  if (isLoading || !settings) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700">Naziv firme</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">PIB</label>
          <input
            value={taxId}
            onChange={(e) => setTaxId(e.target.value)}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Matični broj</label>
          <input
            value={registrationNumber}
            onChange={(e) => setRegistrationNumber(e.target.value)}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Tekući račun - unesi "265405031000057176" ili "265-4050310000571-76"
          </label>
          <input
            value={bankAccount}
            onChange={(e) => setBankAccount(e.target.value)}
            placeholder="265-4050310000571-76"
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
          {settings.bankAccountIban && (
            <p className="mt-1 text-xs text-slate-400">
              IBAN (automatski izračunat): <span className="font-mono">{settings.bankAccountIban}</span>
            </p>
          )}
          {taxId && (
            // 04.10.2026 - ispravljena greska: adresa je PO FIRMI (PIB), ne
            // po racunu - stari oblik <PIB>_<racun>@import.hafiskal.rs nije
            // taj koji stvarno radi (vidi isti fix u BankAccountsSection.tsx
            // i claude/obrada-izvoda-minimax-odrediste.md).
            <div className="mt-1.5 flex items-center gap-1.5 text-sm text-slate-600">
              <span>
                Email za uvoz izvoda:{" "}
                <span className="font-mono font-medium text-slate-800">
                  obradaizvoda_{taxId}@import.hafiskal.rs
                </span>
              </span>
              <button
                type="button"
                onClick={() =>
                  navigator.clipboard.writeText(`obradaizvoda_${taxId}@import.hafiskal.rs`).catch(() => undefined)
                }
                className="text-slate-400 hover:text-brand"
                title="Kopiraj"
              >
                <Copy className="h-4 w-4" aria-hidden />
              </button>
            </div>
          )}
        </div>

        <div className="sm:col-span-2">
          <BankAccountsSection />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Format šifre artikla (kad se ne unese ručno)
          </label>
          <select
            value={skuFormat}
            onChange={(e) => setSkuFormat(e.target.value as SkuFormat)}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          >
            <option value={SkuFormat.NUMERIC}>Numerička (npr. 048213)</option>
            <option value={SkuFormat.ALPHANUMERIC}>Alfanumerička (npr. 7K2QX9AB)</option>
          </select>
        </div>
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Numeracija dokumenata</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Prefiks za fakture</label>
            <input
              value={invoiceNumberPrefix}
              onChange={(e) => setInvoiceNumberPrefix(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
            <label className="mb-1 mt-2 block text-xs font-medium text-slate-500">
              Sledeći broj (ako nastavljate numeraciju iz starog programa)
            </label>
            <input
              type="number"
              min={1}
              value={invoiceNumberStart}
              onChange={(e) => setInvoiceNumberStart(e.target.value)}
              placeholder="npr. 501"
              className="h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Prefiks za ponude</label>
            <input
              value={quoteNumberPrefix}
              onChange={(e) => setQuoteNumberPrefix(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
            <label className="mb-1 mt-2 block text-xs font-medium text-slate-500">
              Sledeći broj (ako nastavljate numeraciju iz starog programa)
            </label>
            <input
              type="number"
              min={1}
              value={quoteNumberStart}
              onChange={(e) => setQuoteNumberStart(e.target.value)}
              placeholder="npr. 501"
              className="h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Prefiks za otpremnice</label>
            <input
              value={deliveryNoteNumberPrefix}
              onChange={(e) => setDeliveryNoteNumberPrefix(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
            <p className="mt-2 text-xs text-slate-400">
              Otpremnica prati redni broj fakture - nema svoju sekvencu.
            </p>
          </div>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={includeYearInNumber}
            onChange={(e) => setIncludeYearInNumber(e.target.checked)}
            className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
          />
          Dodaj godinu na kraj broja (npr. {invoiceNumberPrefix || "FAK"}-0001-{new Date().getFullYear()})
        </label>
        <p className="mt-1 text-xs text-slate-400">
          Broj otpremnice automatski prati broj fakture (isti redni broj/godina, samo prefiks
          otpremnice) - npr. {invoiceNumberPrefix || "FAK"}-0001
          {includeYearInNumber ? `-${new Date().getFullYear()}` : ""} → {deliveryNoteNumberPrefix || "OTP"}
          -0001{includeYearInNumber ? `-${new Date().getFullYear()}` : ""}.
        </p>
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800">Poslovna lokacija</h2>
          {pfrSettings?.businessLocation && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setLocationAddress(pfrSettings.businessLocation!)}
            >
              Povuci iz podešavanja kase
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Naziv lokacije</label>
            <input
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Adresa</label>
            <input
              value={locationAddress}
              onChange={(e) => setLocationAddress(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
        </div>
        {!pfrSettings?.businessLocation && (
          <p className="mt-2 text-xs text-slate-400">
            "Povuci iz podešavanja kase" se pojavljuje kad je otpremljena PDF potvrda Poreske uprave
            (tab "Fiskalizacija (PFR)") - iz nje se čita adresa poslovne jedinice.
          </p>
        )}
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Adresa i kontakt firme ("Moj profil")</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="sm:col-span-3">
            <label className="mb-1 block text-sm font-medium text-slate-700">Ulica</label>
            <input
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Broj</label>
            <input
              value={houseNumber}
              onChange={(e) => setHouseNumber(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Poštanski broj</label>
            <input
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Mesto</label>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Telefon</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">PDV i prikaz</h2>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isVatRegistered}
              onChange={(e) => setIsVatRegistered(e.target.checked)}
              className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
            />
            Obveznik PDV-a (ako isključite, PDV se neće obračunavati na fakturama/ponudama)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={showImagesOnQuotes}
              onChange={(e) => setShowImagesOnQuotes(e.target.checked)}
              className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
            />
            Prikaži slike artikala na ponudama (PDF)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={showImagesOnInvoices}
              onChange={(e) => setShowImagesOnInvoices(e.target.checked)}
              className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
            />
            Prikaži slike artikala na fakturama (PDF)
          </label>
        </div>
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Logo firme</h2>
        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setUploadingLogo(true);
              uploadLogo.mutate(file, { onSettled: () => setUploadingLogo(false) });
            }
            e.target.value = "";
          }}
        />
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-32 items-center justify-center overflow-hidden rounded-lg border border-border bg-app-bg">
            {settings.logoUrl ? (
              <img src={settings.logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-xs text-slate-400">Bez logoa</span>
            )}
          </div>
          <Button
            variant="secondary"
            disabled={uploadingLogo}
            onClick={() => logoInputRef.current?.click()}
          >
            {uploadingLogo ? "Otpremam..." : "Otpremi logo"}
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Prikazuje se na PDF-u ponuda/faktura umesto naziva firme, na vrhu dokumenta.
        </p>
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={showLogoOnReceipt}
            onChange={(e) => setShowLogoOnReceipt(e.target.checked)}
            className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
          />
          Prikaži logo firme na vrhu fiskalnog računa
        </label>
        <p className="mt-1 text-xs text-slate-400">
          Logo se štampa iznad "ФИСКАЛНИ РАЧУН" zaglavlja na fiskalnom računu (zahteva otpremljen
          logo iznad).
        </p>
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Tekst za prihvatanje ponude</h2>
        <textarea
          value={quoteAcceptanceText}
          onChange={(e) => setQuoteAcceptanceText(e.target.value)}
          rows={3}
          placeholder='Npr. "Prihvatanjem ove ponude potvrđujete saglasnost sa navedenim uslovima."'
          className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
        />
        <p className="mt-1 text-xs text-slate-400">
          Prikazuje se kupcu na javnom linku, iznad dugmadi "Prihvati"/"Odbij".
        </p>
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">
          Podrazumevani tekstovi (napomena i uslovi plaćanja)
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Napomena - ponude</label>
            <textarea
              value={defaultQuoteNote}
              onChange={(e) => setDefaultQuoteNote(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Uslovi plaćanja / rok važenja - ponude
            </label>
            <textarea
              value={defaultQuotePaymentTerms}
              onChange={(e) => setDefaultQuotePaymentTerms(e.target.value)}
              rows={2}
              placeholder="Npr. Rok važenja ponude je 15 dana od datuma izdavanja."
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Napomena - fakture</label>
            <textarea
              value={defaultInvoiceNote}
              onChange={(e) => setDefaultInvoiceNote(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Uslovi plaćanja - fakture</label>
            <textarea
              value={defaultInvoicePaymentTerms}
              onChange={(e) => setDefaultInvoicePaymentTerms(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-700">Napomena - otpremnice</label>
            <textarea
              value={defaultDeliveryNoteNote}
              onChange={(e) => setDefaultDeliveryNoteNote(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Ovi tekstovi se automatski upisuju u svaki NOVI dokument tog tipa - i dalje se mogu ručno
          izmeniti po dokumentu.
        </p>
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Kasa (POS) - unos količine</h2>
        <select
          value={posQuantityMode}
          onChange={(e) => setPosQuantityMode(e.target.value as PosQuantityMode)}
          className="h-10 w-full max-w-sm rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        >
          <option value={PosQuantityMode.WHOLE}>Celi brojevi (dugmići +/- po 1 komad)</option>
          <option value={PosQuantityMode.DECIMAL}>Decimalni brojevi (za robu na gramažu/kilažu)</option>
        </select>
        <p className="mt-1 text-xs text-slate-400">
          "Decimalni brojevi" na kasi zamenjuje +/- dugmiće poljem za slobodan unos količine (npr.
          0.350 za 350g).
        </p>
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Magacinsko poslovanje</h2>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={usesWarehouse}
            onChange={(e) => setUsesWarehouse(e.target.checked)}
            className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
          />
          Firma koristi magacin
        </label>
        <p className="mt-1 text-xs text-slate-400">
          Ako je uključeno, prodaja preko fakture dobija dugme "Nalog magacinu" za formalno izdavanje
          robe sa lagera. Ako je isključeno, prodaja ostaje jednostavna (faktura je dovoljna).
        </p>
      </div>

      {save.isError && (
        <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          Čuvanje nije uspelo. Proverite unete podatke.
        </p>
      )}
      {save.isSuccess && (
        <p className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Sačuvano.</p>
      )}

      <div className="mt-5 flex justify-end">
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          Sačuvaj
        </Button>
      </div>
    </div>
  );
}

type SmtpProviderKey = "custom" | "gmail" | "yahoo" | "microsoft";

/**
 * 30.09.2026 (korisnički zahtev: "nastavi ovo sa mailom gmail yahoo
 * microsoft") - brzo podešavanje SMTP servera za tri najčešća javna
 * provajdera, da korisnik ne mora ručno da zna host/port/SSL. Ovo SAMO
 * popunjava postojeća polja (host/port/secure) - ne dodaje novo polje u
 * bazu niti novu rutu, jer `Company.smtp*` (vidi company.service.ts) već
 * prihvata proizvoljan SMTP server, uključujući ove.
 *
 * VAŽNO (provereno u kodu, `email.service.ts`): Finora šalje mejl preko
 * običnog `nodemailer` SMTP AUTH-a (korisničko ime + lozinka), BEZ OAuth2.
 * To znači:
 * - Gmail i Yahoo TRAŽE "Lozinku za aplikacije" (App Password) - obična
 *   lozinka naloga neće raditi kad je uključena dvofaktorska prijava (a
 *   oba provajdera je sve više i traže i bez nje).
 * - Microsoft (Outlook/Office 365) je kod VEĆINE naloga UGASIO običnu
 *   (SMTP AUTH) prijavu u korist OAuth2 - ako slanje ne uspe ni sa
 *   ispravnom lozinkom, IT administrator tog naloga mora ručno ponovo
 *   uključiti "SMTP AUTH" za taj poštanski sandučić u Microsoft 365 admin
 *   centru; ako organizacija to ne dozvoljava (Security Defaults/
 *   Conditional Access), ovaj način slanja neće raditi UOPŠTE dok se ne
 *   pređe na OAuth2 (što Finora danas ne podržava).
 */
const SMTP_PROVIDERS: Record<
  Exclude<SmtpProviderKey, "custom">,
  { label: string; host: string; port: number; secure: boolean; help: string }
> = {
  gmail: {
    label: "Gmail",
    host: "smtp.gmail.com",
    port: 587,
    secure: false,
    help:
      "Gmail zahteva \"Lozinku za aplikacije\" (App Password), ne običnu Google lozinku - uključi dvofaktorsku prijavu na nalogu, pa je napravi na myaccount.google.com/apppasswords.",
  },
  yahoo: {
    label: "Yahoo",
    host: "smtp.mail.yahoo.com",
    port: 587,
    secure: false,
    help:
      "Yahoo takođe zahteva \"Lozinku za aplikacije\" (App Password) - napravi je u podešavanjima naloga (Account Security → Generate app password), obična lozinka neće raditi.",
  },
  microsoft: {
    label: "Microsoft (Outlook / Office 365)",
    host: "smtp.office365.com",
    port: 587,
    secure: false,
    help:
      "Microsoft je kod većine naloga ugasio običnu (SMTP AUTH) prijavu - ako slanje ne uspe ni sa ispravnom lozinkom, administrator naloga mora ponovo uključiti \"SMTP AUTH\" za taj mejl u Microsoft 365 admin centru.",
  },
};

function EmailTab() {
  const queryClient = useQueryClient();
  const { data: smtpSettings } = useQuery({
    queryKey: ["smtp-settings"],
    queryFn: () => api.getSmtpSettings(),
  });
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPass, setSmtpPass] = useState("");
  const [smtpFrom, setSmtpFrom] = useState("");
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [smtpProvider, setSmtpProvider] = useState<SmtpProviderKey>("custom");

  useEffect(() => {
    if (smtpSettings) {
      setSmtpHost(smtpSettings.smtpHost ?? "");
      setSmtpPort(smtpSettings.smtpPort ? String(smtpSettings.smtpPort) : "587");
      setSmtpUser(smtpSettings.smtpUser ?? "");
      setSmtpFrom(smtpSettings.smtpFrom ?? "");
      setSmtpSecure(smtpSettings.smtpSecure);
      // Ako sačuvan host odgovara tačno jednom od poznatih provajdera,
      // izbornik ga prepoznaje umesto da uvek pokazuje "Prilagođeno".
      const host = smtpSettings.smtpHost ?? "";
      if (host === SMTP_PROVIDERS.gmail.host) setSmtpProvider("gmail");
      else if (host === SMTP_PROVIDERS.yahoo.host) setSmtpProvider("yahoo");
      else if (host === SMTP_PROVIDERS.microsoft.host) setSmtpProvider("microsoft");
      else setSmtpProvider("custom");
    }
  }, [smtpSettings]);

  const handleProviderChange = (key: SmtpProviderKey) => {
    setSmtpProvider(key);
    if (key !== "custom") {
      const preset = SMTP_PROVIDERS[key];
      setSmtpHost(preset.host);
      setSmtpPort(String(preset.port));
      setSmtpSecure(preset.secure);
    }
  };

  const saveSmtp = useMutation({
    mutationFn: () =>
      api.updateSmtpSettings({
        smtpHost: smtpHost || undefined,
        smtpPort: smtpPort ? Number(smtpPort) : undefined,
        smtpUser: smtpUser || undefined,
        smtpPass: smtpPass || undefined,
        smtpFrom: smtpFrom || undefined,
        smtpSecure,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["smtp-settings"], updated);
      setSmtpPass("");
    },
  });

  const { data: companySettings } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });
  const saveDesign = useMutation({
    mutationFn: (design: EmailTemplateDesign) => api.updateCompanySettings({ emailTemplateDesign: design }),
    onSuccess: (updated) => queryClient.setQueryData(["company-settings"], updated),
  });

  return (
    <>
    <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <p className="mb-4 text-xs text-slate-500">
        {smtpSettings?.hasSmtpConfig
          ? "SMTP je podešen. Ostavi lozinku praznu da zadržiš postojeću."
          : "SMTP nije podešen - dok se ne unese, koristiće se podrazumevani nalog iz .env fajla (ako postoji)."}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700">Provajder (brzo podešavanje)</label>
          <select
            value={smtpProvider}
            onChange={(e) => handleProviderChange(e.target.value as SmtpProviderKey)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm focus:border-brand focus:outline-none"
          >
            <option value="custom">Prilagođeno (ručno unesi server ispod)</option>
            <option value="gmail">Gmail</option>
            <option value="yahoo">Yahoo</option>
            <option value="microsoft">Microsoft (Outlook / Office 365)</option>
          </select>
          {smtpProvider !== "custom" && (
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {SMTP_PROVIDERS[smtpProvider].help}
            </p>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700">SMTP server (host)</label>
          <input
            value={smtpHost}
            onChange={(e) => setSmtpHost(e.target.value)}
            placeholder="npr. smtp.gmail.com"
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Port</label>
          <input
            value={smtpPort}
            onChange={(e) => setSmtpPort(e.target.value)}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <label className="mt-6 flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={smtpSecure}
            onChange={(e) => setSmtpSecure(e.target.checked)}
            className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
          />
          Koristi SSL (obično za port 465)
        </label>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Korisničko ime</label>
          <input
            value={smtpUser}
            onChange={(e) => setSmtpUser(e.target.value)}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Lozinka</label>
          <input
            type="password"
            value={smtpPass}
            onChange={(e) => setSmtpPass(e.target.value)}
            placeholder={smtpSettings?.hasSmtpConfig ? "•••••••• (nepromenjeno)" : ""}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700">"Šalje" adresa (From)</label>
          <input
            value={smtpFrom}
            onChange={(e) => setSmtpFrom(e.target.value)}
            placeholder="fakture@tvojafirma.rs"
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      {saveSmtp.isError && (
        <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          Čuvanje SMTP podešavanja nije uspelo.
        </p>
      )}
      {saveSmtp.isSuccess && (
        <p className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Sačuvano.</p>
      )}

      <div className="mt-5 flex justify-end">
        <Button disabled={saveSmtp.isPending} onClick={() => saveSmtp.mutate()}>
          Sačuvaj SMTP podešavanja
        </Button>
      </div>
    </div>

    <div className="mt-5 rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <h2 className="mb-1 text-sm font-semibold text-slate-800">Izgled email-a</h2>
      <p className="mb-4 text-xs text-slate-500">
        Kada se pošalje ponuda/faktura emailom, može se koristiti prost tekst (podrazumevano) ili
        jedan od dva bogata HTML dizajna, sa QR kodom za plaćanje/prihvatanje ugrađenim u sam email.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {(
          [
            { key: "NONE", label: "Prost tekst (podrazumevano)", image: null },
            { key: "DESIGN_1", label: "Dizajn 1", image: "/email-designs/design-1.png" },
            { key: "DESIGN_2", label: "Dizajn 2", image: "/email-designs/design-2.png" },
          ] as { key: EmailTemplateDesign; label: string; image: string | null }[]
        ).map((option) => {
          const isSelected = (companySettings?.emailTemplateDesign ?? "NONE") === option.key;
          return (
            <button
              key={option.key}
              onClick={() => saveDesign.mutate(option.key)}
              disabled={saveDesign.isPending}
              className={`rounded-xl border-2 p-2 text-left transition-colors ${
                isSelected ? "border-brand bg-brand-light" : "border-border hover:border-slate-300"
              }`}
            >
              <div className="mb-2 flex h-32 items-center justify-center overflow-hidden rounded-lg bg-app-bg">
                {option.image ? (
                  <img src={option.image} alt={option.label} className="h-full w-full object-cover object-top" />
                ) : (
                  <span className="px-3 text-center text-xs text-slate-400">Bez posebnog dizajna</span>
                )}
              </div>
              <p className={`text-sm font-medium ${isSelected ? "text-brand" : "text-slate-700"}`}>
                {option.label}
                {isSelected && " ✓"}
              </p>
            </button>
          );
        })}
      </div>
    </div>
    </>
  );
}

function FiskalizacijaSwitch() {
  const [sub, setSub] = useState<"pfr" | "lpfr">("pfr");
  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-border">
        {([["pfr", "PFR"], ["lpfr", "L-PFR"]] as const).map(([k, l]) => (
          <button
            key={k}
            onClick={() => setSub(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${sub === k ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            {l}
          </button>
        ))}
      </div>
      {sub === "pfr" ? <FiskalizacijaTab /> : <LpfrTab />}
    </div>
  );
}

function FiskalizacijaTab() {
  const queryClient = useQueryClient();
  const pfrFileInputRef = useRef<HTMLInputElement>(null);
  const pfrCertParseInputRef = useRef<HTMLInputElement>(null);
  const pfrBundleArchiveInputRef = useRef<HTMLInputElement>(null);
  const pfrBundleDocumentInputRef = useRef<HTMLInputElement>(null);
  const { data: pfrSettings } = useQuery({
    queryKey: ["pfr-settings"],
    queryFn: () => api.getPfrSettings(),
  });
  const { data: companySettings } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });
  const [pfrMode, setPfrMode] = useState<PfrMode>(PfrMode.SANDBOX);
  const [pfrBaseUrl, setPfrBaseUrl] = useState("");
  const [pfrUploading, setPfrUploading] = useState(false);
  const [pfrCertParsing, setPfrCertParsing] = useState(false);
  const [parsedCertInfo, setParsedCertInfo] = useState<{
    jid: string | null;
    businessLocation: string | null;
  } | null>(null);
  const [defaultCombinedPaymentMethod, setDefaultCombinedPaymentMethod] = useState<PaymentMethod>(
    PaymentMethod.CASH,
  );
  // Ručni override PAK/lozinke - vidi razgovor "mac verify failure" (PFX
  // se ne može dešifrovati - lozinka koju je eksterni servis izvukao iz
  // PDF-a ne odgovara STVARNOJ lozinci .pfx fajla, npr. ako PDF i .pfx nisu iz istog
  // izdavanja sertifikata, ili je OCR/parsiranje pogrešno pročitalo
  // poneki karakter). Polja su i dalje ZAKLJUČANA po difoltu (da se izbegne
  // greška pri prekucavanju), ali ovo je izlaz za slučaj kad automatski
  // unos ipak ne odgovara stvarnom fajlu.
  const [manualOverride, setManualOverride] = useState(false);
  const [manualPak, setManualPak] = useState("");
  const [manualPassword, setManualPassword] = useState("");

  // Kombinovano otpremanje: ZIP arhiva sertifikata (dobijena od Poreske
  // uprave, sadrži .p12/.pfx u više formata) + PDF potvrda, u jednom
  // koraku - backend sam izvuče .pfx iz ZIP-a i PAK/lozinku iz PDF-a.
  const [pfrBundleArchive, setPfrBundleArchive] = useState<File | null>(null);
  const [pfrBundleDocument, setPfrBundleDocument] = useState<File | null>(null);
  const [bundleParsedInfo, setBundleParsedInfo] = useState<{
    jid: string | null;
    businessLocation: string | null;
    pfxFileName: string | null;
  } | null>(null);

  useEffect(() => {
    if (pfrSettings) {
      setPfrMode(pfrSettings.pfrMode);
      setPfrBaseUrl(pfrSettings.pfrBaseUrl ?? "");
    }
  }, [pfrSettings]);

  useEffect(() => {
    if (companySettings) {
      setDefaultCombinedPaymentMethod(companySettings.defaultCombinedPaymentMethod);
    }
  }, [companySettings]);

  const savePfr = useMutation({
    mutationFn: () =>
      api.updatePfrSettings({
        pfrMode,
        pfrBaseUrl: pfrBaseUrl || undefined,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["pfr-settings"], updated);
    },
  });

  const uploadPfrCert = useMutation({
    mutationFn: (file: File) => api.uploadPfrCertificate(file),
    onSuccess: (updated) => queryClient.setQueryData(["pfr-settings"], updated),
  });

  // Umesto ručnog kucanja PAK/lozinke (lako se pogreši), korisnik otprema
  // PDF potvrdu koju dobije mejlom od Poreske uprave ("Ваши сертификати су
  // израђени...") - eksterni servis je pročita i vrati PAK/lozinku, koje se ovde
  // automatski upisuju. Polja za PAK/lozinku su zato uvek zaključana za
  // ručni unos (readOnly ispod) - vidi razgovor.
  const parsePfrCert = useMutation({
    mutationFn: (file: File) => api.parsePfrCertificateDocument(file),
    onSuccess: (updated) => {
      queryClient.setQueryData(["pfr-settings"], updated);
      setParsedCertInfo({ jid: updated.parsed.jid, businessLocation: updated.parsed.businessLocation });
    },
  });

  const uploadPfrBundle = useMutation({
    mutationFn: (files: { archive?: File; document?: File }) => api.uploadPfrCertificateBundle(files),
    onSuccess: (updated) => {
      queryClient.setQueryData(["pfr-settings"], updated);
      setBundleParsedInfo({
        jid: updated.parsed?.jid ?? null,
        businessLocation: updated.parsed?.businessLocation ?? null,
        pfxFileName: updated.pfxFileName,
      });
      setPfrBundleArchive(null);
      setPfrBundleDocument(null);
    },
  });

  const saveManualPfrCredentials = useMutation({
    mutationFn: () =>
      api.updatePfrSettings({
        ...(manualPak ? { pfrApiKey: manualPak } : {}),
        ...(manualPassword ? { pfrCertificatePassword: manualPassword } : {}),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["pfr-settings"], updated);
      setManualPak("");
      setManualPassword("");
      setManualOverride(false);
    },
  });

  const saveCombinedPayment = useMutation({
    mutationFn: () => api.updateCompanySettings({ defaultCombinedPaymentMethod }),
    onSuccess: (updated) => queryClient.setQueryData(["company-settings"], updated),
  });

  return (
    <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <p className="mb-4 text-xs text-slate-500">
        {pfrSettings?.hasCertificate
          ? "Sertifikat je otpremljen."
          : "Sertifikat nije otpremljen - fiskalizacija neće raditi dok se ne doda."}{" "}
        {pfrSettings?.hasApiKey ? "PAK/PAC kod je podešen." : "PAK/PAC kod nije podešen."}
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Okruženje</label>
          <select
            value={pfrMode}
            onChange={(e) => setPfrMode(e.target.value as PfrMode)}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          >
            <option value={PfrMode.SANDBOX}>Sandbox (test)</option>
            <option value={PfrMode.PROD}>Produkcija</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Base URL (opciono, override)
          </label>
          <input
            value={pfrBaseUrl}
            onChange={(e) => setPfrBaseUrl(e.target.value)}
            placeholder="https://vsdc.sandbox.suf.purs.gov.rs"
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">PAK/PAC kod</label>
          <input
            value={manualOverride ? manualPak : pfrSettings?.hasApiKey ? "•••••••• (podešeno preko potvrde)" : ""}
            onChange={manualOverride ? (e) => setManualPak(e.target.value) : undefined}
            readOnly={!manualOverride}
            placeholder={manualOverride ? "Unesi tačan PAK/PAC kod" : "Otpremi potvrdu Poreske uprave ispod"}
            className={`h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none ${
              manualOverride ? "bg-white" : "bg-app-bg text-slate-500"
            }`}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Lozinka sertifikata</label>
          <input
            type={manualOverride ? "text" : "password"}
            value={manualOverride ? manualPassword : pfrSettings?.hasApiKey ? "••••••••" : ""}
            onChange={manualOverride ? (e) => setManualPassword(e.target.value) : undefined}
            readOnly={!manualOverride}
            placeholder={manualOverride ? "Unesi tačnu lozinku .pfx fajla" : "Otpremi potvrdu Poreske uprave ispod"}
            className={`h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none ${
              manualOverride ? "bg-white" : "bg-app-bg text-slate-500"
            }`}
          />
        </div>

        <div className="sm:col-span-2 -mt-2">
          {!manualOverride ? (
            <button
              type="button"
              onClick={() => setManualOverride(true)}
              className="text-xs font-medium text-brand hover:underline"
            >
              PAK/lozinka ne odgovaraju stvarnom sertifikatu? Izmeni ručno.
            </button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                disabled={saveManualPfrCredentials.isPending || (!manualPak && !manualPassword)}
                onClick={() => saveManualPfrCredentials.mutate()}
              >
                {saveManualPfrCredentials.isPending ? "Čuvam..." : "Sačuvaj ručni unos"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setManualOverride(false);
                  setManualPak("");
                  setManualPassword("");
                }}
              >
                Otkaži
              </Button>
              {saveManualPfrCredentials.isError && (
                <p className="w-full text-sm text-red-700">
                  {saveManualPfrCredentials.error instanceof Error
                    ? saveManualPfrCredentials.error.message
                    : "Čuvanje nije uspelo."}
                </p>
              )}
            </div>
          )}
          <p className="mt-1 text-xs text-slate-400">
            Napomena: ako fiskalizacija javlja grešku "mac verify failure", to znači da PAK/lozinka ne
            odgovaraju TAČNO otpremljenom .pfx fajlu (npr. PDF potvrda i .pfx nisu iz istog izdavanja
            sertifikata, ili je automatsko očitavanje pogrešno pročitalo neki karakter) - proveri tačan
            tekst u PDF-u i unesi ga ovde ručno.
          </p>
        </div>

        <div className="sm:col-span-2 rounded-lg border border-brand/40 bg-brand/5 p-4">
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Preporučeno: ZIP arhiva sertifikata + PDF potvrda (u jednom koraku)
          </label>
          <p className="mb-3 text-xs text-slate-500">
            Otpremi ZIP arhivu koju dobiješ uz sertifikat (sadrži .p12/.pfx fajlove) i PDF potvrdu Poreske
            uprave zajedno - sistem sam izvuče .pfx iz arhive i PAK/lozinku iz PDF-a, tako da se izbegava
            neusklađenost između ručno otpremljenog .pfx fajla i PAK/lozinke iz drugog izvora. Možeš priložiti
            samo jedan od dva fajla ako drugi već imaš podešen.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={pfrBundleArchiveInputRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => {
                setPfrBundleArchive(e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
            <Button variant="secondary" onClick={() => pfrBundleArchiveInputRef.current?.click()}>
              {pfrBundleArchive ? `ZIP: ${pfrBundleArchive.name}` : "Izaberi ZIP arhivu"}
            </Button>
            <input
              ref={pfrBundleDocumentInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => {
                setPfrBundleDocument(e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
            <Button variant="secondary" onClick={() => pfrBundleDocumentInputRef.current?.click()}>
              {pfrBundleDocument ? `PDF: ${pfrBundleDocument.name}` : "Izaberi PDF potvrdu"}
            </Button>
            <Button
              disabled={uploadPfrBundle.isPending || (!pfrBundleArchive && !pfrBundleDocument)}
              onClick={() =>
                uploadPfrBundle.mutate({
                  archive: pfrBundleArchive ?? undefined,
                  document: pfrBundleDocument ?? undefined,
                })
              }
            >
              {uploadPfrBundle.isPending ? "Otpremam..." : "Otpremi zajedno"}
            </Button>
          </div>

          {uploadPfrBundle.isError && (
            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {uploadPfrBundle.error instanceof Error
                ? uploadPfrBundle.error.message
                : "Otpremanje nije uspelo."}
            </p>
          )}
          {uploadPfrBundle.isSuccess && (
            <div className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              <p>Podaci su uspešno izvučeni i sačuvani.</p>
              {bundleParsedInfo?.pfxFileName && (
                <p className="mt-1 text-xs">Sertifikat iz arhive: {bundleParsedInfo.pfxFileName}</p>
              )}
              {bundleParsedInfo?.jid && <p className="text-xs">ЈИД: {bundleParsedInfo.jid}</p>}
              {bundleParsedInfo?.businessLocation && (
                <p className="text-xs">Poslovni prostor: {bundleParsedInfo.businessLocation}</p>
              )}
            </div>
          )}
        </div>

        <div className="sm:col-span-2 rounded-lg border border-dashed border-border p-4">
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Samo PDF potvrda Poreske uprave (opciono, ako ne koristiš opciju iznad)
          </label>
          <p className="mb-2 text-xs text-slate-500">
            Otpremi PDF koji dobiješ mejlom od Poreske uprave ("Ваши сертификати су израђени...") - PAK i
            lozinka se automatski očitaju i upišu, da se izbegne greška pri ručnom prekucavanju. PAK/lozinka
            polja iznad su zato zaključana - menjaju se samo novom potvrdom.
          </p>
          <input
            ref={pfrCertParseInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setPfrCertParsing(true);
                parsePfrCert.mutate(file, { onSettled: () => setPfrCertParsing(false) });
              }
              e.target.value = "";
            }}
          />
          <Button
            variant="secondary"
            disabled={pfrCertParsing}
            onClick={() => pfrCertParseInputRef.current?.click()}
          >
            {pfrCertParsing ? "Očitavam potvrdu..." : "Otpremi potvrdu Poreske uprave"}
          </Button>

          {parsePfrCert.isError && (
            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {parsePfrCert.error instanceof Error
                ? parsePfrCert.error.message
                : "Očitavanje dokumenta nije uspelo."}
            </p>
          )}
          {parsePfrCert.isSuccess && (
            <div className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              <p>PAK i lozinka su uspešno očitani i sačuvani.</p>
              {parsedCertInfo?.jid && <p className="mt-1 text-xs">ЈИД: {parsedCertInfo.jid}</p>}
              {parsedCertInfo?.businessLocation && (
                <p className="text-xs">Poslovni prostor: {parsedCertInfo.businessLocation}</p>
              )}
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Samo .pfx fajl (opciono, ako ne koristiš opciju iznad)
          </label>
          <input
            ref={pfrFileInputRef}
            type="file"
            accept=".pfx,.p12"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setPfrUploading(true);
                uploadPfrCert.mutate(file, { onSettled: () => setPfrUploading(false) });
              }
              e.target.value = "";
            }}
          />
          <Button
            variant="secondary"
            disabled={pfrUploading}
            onClick={() => pfrFileInputRef.current?.click()}
          >
            {pfrUploading ? "Otpremam..." : "Otpremi .pfx fajl"}
          </Button>
        </div>
      </div>

      {savePfr.isError && (
        <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Čuvanje nije uspelo.</p>
      )}
      {savePfr.isSuccess && (
        <p className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Sačuvano.</p>
      )}

      <div className="mt-5 flex justify-end">
        <Button disabled={savePfr.isPending} onClick={() => savePfr.mutate()}>
          Sačuvaj PFR podešavanja
        </Button>
      </div>

      <FiscalDevicesSection />

      <div className="mt-5 border-t border-border pt-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Kombinovano plaćanje</h2>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Podrazumevani način naplate za ostatak
        </label>
        <select
          value={defaultCombinedPaymentMethod}
          onChange={(e) => setDefaultCombinedPaymentMethod(e.target.value as PaymentMethod)}
          className="h-10 w-64 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        >
          <option value="CASH">Gotovina</option>
          <option value="CARD">Kartica</option>
          <option value="BANK_TRANSFER">Uplata na račun</option>
          <option value="VOUCHER">Vaučer</option>
        </select>
        <p className="mt-1 text-xs text-slate-400">
          Kod kombinovanog plaćanja na kasi, kad se unese deo iznosa na neki način plaćanja,
          preostali iznos se automatski dodeljuje ovom (podrazumevano gotovina).
        </p>
        <div className="mt-3 flex justify-end">
          <Button disabled={saveCombinedPayment.isPending} onClick={() => saveCombinedPayment.mutate()}>
            Sačuvaj
          </Button>
        </div>
      </div>

    </div>
  );
}

function SefTab() {
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState("");
  const { data: settings } = useQuery({
    queryKey: ["sef-settings"],
    queryFn: () => api.getSefSettings(),
  });
  const { data: companySettings } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });
  const [autoSendToSefOnAcceptance, setAutoSendToSefOnAcceptance] = useState(false);

  useEffect(() => {
    if (companySettings) {
      setAutoSendToSefOnAcceptance(companySettings.autoSendToSefOnAcceptance);
    }
  }, [companySettings]);

  const save = useMutation({
    mutationFn: (payload: { apiKey?: string; sefMode?: SefMode }) => api.updateSefSettings(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sef-settings"] });
      setApiKey("");
    },
  });

  const saveAutomation = useMutation({
    mutationFn: () => api.updateCompanySettings({ autoSendToSefOnAcceptance }),
    onSuccess: (updated) => queryClient.setQueryData(["company-settings"], updated),
  });

  return (
    <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            API ključ {settings?.hasApiKey && "(podešen ✓)"}
          </label>
          <input
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={settings?.hasApiKey ? "•••••••• (unesi novi da zameniš)" : "Nalepi API ključ sa SEF portala"}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Okruženje</label>
          <select
            value={settings?.sefMode ?? SefMode.DEMO}
            onChange={(e) => save.mutate({ sefMode: e.target.value as SefMode })}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          >
            <option value={SefMode.DEMO}>Demo</option>
            <option value={SefMode.PROD}>Produkcija</option>
          </select>
        </div>
      </div>
      <p className="mt-2 text-xs text-slate-400">
        API ključ se generiše na SEF portalu (Podešavanja → Upravljanje API). Demo i produkcioni ključ
        su odvojeni - ne mešati okruženja.
      </p>
      <div className="mt-4 flex justify-end">
        <Button disabled={!apiKey || save.isPending} onClick={() => save.mutate({ apiKey })}>
          Sačuvaj ključ
        </Button>
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">eFakture (SEF) automatizacija</h2>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={autoSendToSefOnAcceptance}
            onChange={(e) => setAutoSendToSefOnAcceptance(e.target.checked)}
            className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
          />
          Kada kupac prihvati ponudu, automatski je pretvori u fakturu i pošalji na SEF
        </label>
        <p className="mt-1 text-xs text-slate-400">
          Radi samo ako i firma ima podešen SEF API ključ i kupac ima obeleženo "Ima eFakture".
        </p>
        <div className="mt-3 flex justify-end">
          <Button disabled={saveAutomation.isPending} onClick={() => saveAutomation.mutate()}>
            Sačuvaj
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CompanySettingsPage() {
  const initialTab = (() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return TABS.some((x) => x.key === t) ? (t as TabKey) : "firma";
    } catch {
      return "firma" as TabKey;
    }
  })();
  const [tab, setTab] = useState<TabKey>(initialTab);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Podešavanja</h1>
        <p className="whitespace-nowrap text-sm text-slate-500">
          Podaci ovde se koriste na PDF dokumentima i pri slanju na SEF/PFR - moraju odgovarati
          stvarnoj registraciji firme.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.key
                ? "border-brand text-brand"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <t.icon className="h-4 w-4" aria-hidden />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "firma" && <FirmaTab />}
      {tab === "nalog" && <AccountTab />}
      {tab === "email" && <EmailTab />}
      {tab === "fiskalizacija" && <FiskalizacijaSwitch />}
      {tab === "sef" && <SefTab />}
      {tab === "minimax" && <MinimaxTab />}
      {tab === "ips-pgw" && <IpsPgwTab />}
      {tab === "kartice" && <CardPaymentsTab />}
      {tab === "couriers" && <CourierSettingsTab />}
      {tab === "print-agent" && <PrintAgentTab />}
      {tab === "kase" && <DevicesTab />}
    </div>
  );
}
